import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, distinctUntilChanged, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

import {
  selectCalLotsSlice,
  selectCalculationAdjustedResult,
  selectIsCalAdjustFinal,
  selectCalculationResult,
  selectPanelName,
  selectPanelSpecsSlice,
  selectPatientFileMetadata,
  selectRefKitLot,
  selectReportContentItemsSlice,
  selectSampleDataLoading,
  selectAssayDetailsColumnSelection,
  selectSampleDataAdjusted,
  selectSampleDataOriginal,
  selectSampleDataStatus,
  selectRunType,
  selectTestSpec,
  selectQcLotsSlice,
  selectAppState,
  selectIsStudySaved
} from '../../state/selectors';
import { ensureSampleData, setAssayDetailsColumnSelection } from '../../state/actions';
import { FileMetaData, ReportContentItem } from '../../../shared/models/run.model';
import { AssayDetails, CalculationResult, RegressionStats, ReportItems, RunStatus, SampleSummaryResult } from '../../../shared/models/result.model';
import { ColumnSelection } from '../../../shared/models/dialog.model';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { trackByFn, getStatusClassFromResult, runValidityPassFailAlertCount, resultPassFailAlert, normalizeStatusValue } from '../../../shared/utils/assay-helper ';
import { Panel } from '../../../shared/models/assays.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { QcLot } from '../../../shared/models/qc-lot.model';
import { AssayWorkbookExportService } from '../services/assay-workbook-export.service';
import { adjustedColumnLabel } from '../../../shared/utils/helper';
import { isStabilityRunType } from '../../../shared/utils/run-type.util';

/**
 * Component for displaying Assay Details.
 * It loads relevant calculation results, report content, and metadata
 * related to the selected study.
 * 
 * Supports print-friendly view modes.
 */
@Component({
  selector: 'app-assay-details-failures',
  templateUrl: './assay-details-failures.component.html',
  styleUrl: './assay-details-failures.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssayDetailsFailuresComponent implements OnInit, OnDestroy, AfterViewInit {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  @Input() tableId = 'assay-table';
  @Input() exportMode = false;
  @ViewChild('assayDetailsScroll', { static: false }) assayDetailsScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild('assayDetailsTable', { static: false }) assayDetailsTableRef?: ElementRef<HTMLTableElement>;
  // In print mode the table is rendered once per chunk page, and every instance needs pruning.
  @ViewChildren('assayDetailsTable') assayDetailsTableRefs?: QueryList<ElementRef<HTMLTableElement>>;

  panelName$ = this.store.select(selectPanelName);
  sectionVisibility = new Array<boolean>();
  patientFileMetadata: FileMetaData[] = [];
  reportContent: ReportContentItem[] = [];
  filteredReportContent: ReportContentItem[] = [];
  calculationResponse: CalculationResult | null = null;
  calculationAdjustedResponse: CalculationResult | null = null;
  tableColumns: ColumnSelection[] = [];
  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  selectedColumns!: ColumnSelection[];
  isPrintView = false;
  isPrintMode = false;
  printTruncatedReport = true; /** Print Truncated Report checkbox state */

  /** Print View, Print Truncated Report and Export As Workbook are offered for Stability runs only. */
  get showStabilityOutputs(): boolean {
    return isStabilityRunType(this.runType);
  }
  isMenuOpen = false;
  operatorName = '';
  studyName = '';
  specVersion = '';
  appVersion = '';
  protected destroy$ = new Subject<void>();
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  private lastSampleDataFresh: boolean | null = null;
  calResponseFilteredData: AssayDetails[] = [];
  calResponseCombinedData: AssayDetails[] = [];
  calResponseCombinedBackupData: AssayDetails[] = [];
  calResponseFilteredAdjustedData: AssayDetails[] = [];
  calResponseDetails: AssayDetails[] = [];
  samplesList: string[] = [];
  private lastSamplesListSourceAll: AssayDetails[] | null = null;
  private lastSamplesListValueAll: string[] = [];
  private visibleSectionCount = 0;
  private deferredRenderHandle: number | null = null;
  private deferredRenderToken = 0;
  private readonly sampleFieldAliases: Record<string, string> = {};
  getStatusClassFromResult = getStatusClassFromResult;

  /**
   * The row labels here are sample IDs and statistic names; the data columns hold one value each.
   * Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 58, columnWidthMm: 22 };

  trackByPage = (index: number) => index;

  get printPages(): AssayDetails[][] {
    return chunkColumnsForPrint(this.calResponseCombinedData, this.printPageFit);
  }

  /** "Columns 7-12 of 22 · page 2 of 4" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }
  runValidityPassFailAlertCount = runValidityPassFailAlertCount;
  trackByFn = trackByFn;
  trackBySample = (_index: number, sample: string) => sample ?? _index;
  resultPassFailAlert = resultPassFailAlert;
  readonly assayLabelName = 'Assay Details';
  panelSpecs: Panel | null = null;
  qcLots: QcLot[] = [];
  calLots: CalLot[] = [];
  runType = '';
  testSpec = '';
  refKitLot = '';
  private readonly assayDetailsColumnSelection$ = this.store.select(selectAssayDetailsColumnSelection);
  sampleDataLoading = false;
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  private headerResizeObserver?: ResizeObserver;
  sampleDataOriginal: SampleSummaryResult[] = [];
  sampleDataAdjusted: SampleSummaryResult[] = [];
  private readonly runStatusMapCache = new WeakMap<RunStatus[], Map<string, RunStatus>>();
  private readonly regressionStatsMapCache = new WeakMap<RegressionStats[], Map<string, RegressionStats>>();
  private readonly reportItemsMapCache = new WeakMap<ReportItems[], Map<string, ReportItems>>();
  private readonly samplesByReportIdCache = new WeakMap<SampleSummaryResult[], Map<string, SampleSummaryResult[]>>();
  private lastRebuildInputs?: {
    reportContent: ReportContentItem[];
    sampleDataOriginal: SampleSummaryResult[];
    sampleDataAdjusted: SampleSummaryResult[];
    calculationResponse: CalculationResult | null;
    calculationAdjustedResponse: CalculationResult | null;
    panelSpecs: Panel | null;
    calLots: CalLot[];
    qcLots: QcLot[];
    runType: string;
    testSpec: string;
    refKitLot: string;
    columnSelectionKey: string;
    isPrintView: boolean;
  };
  /**
   * 
   * @param router Angular router
   * @param store NGRX store
   * @param appUtilityService Utility service 
   * @param cdRef Change detector reference
   */
  constructor(
    private router: Router,
    private store: Store,
    private appUtilityService: AppUtilityService,
    private assayWorkbookExportService: AssayWorkbookExportService,
    public cdRef: ChangeDetectorRef
  ) { }

  /** Lifecycle hook: Component initialization. */
  ngOnInit() {
    this.store.select(selectIsCalAdjustFinal).pipe(takeUntil(this.destroy$)).subscribe(isFinal => {
      this.isFinal = isFinal;
      this.cdRef.markForCheck();
    });
    this.isPrintView = this.exportMode ? false : this.router?.url?.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;
    this.monitorSampleDataStatus();

    /* eslint-disable @ngrx/avoid-combining-selectors -- Component consumes individual slices to keep OnPush checks cheap. */
    combineLatest([
      this.store.select(selectPatientFileMetadata),
      this.store.select(selectReportContentItemsSlice),
      this.store.select(selectSampleDataOriginal),
      this.store.select(selectSampleDataAdjusted),
      this.store.select(selectCalculationResult),
      this.store.select(selectCalculationAdjustedResult),
      this.store.select(selectPanelSpecsSlice),
      this.store.select(selectCalLotsSlice),
      this.store.select(selectQcLotsSlice),
      this.store.select(selectRunType),
      this.store.select(selectTestSpec),
      this.store.select(selectRefKitLot),
      this.assayDetailsColumnSelection$,
      this.store.select(selectAppState)
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((value, index) => value === curr[index])),
        takeUntil(this.destroy$)
      )
      .subscribe(([
      patientFileMetadata,
      reportContentResponse,
      sampleDataOriginal,
      sampleDataAdjusted,
      calculationResult,
      calculationAdjustedResult,
      panelSpecs,
      calLots,
      qcLots,
      runType,
      testSpec,
      refKitLot,
      assayDetailsColumnSelection,
      appState
      ]) => {
        // Cache each slice locally so the template and helper methods can stay synchronous while still
        // taking advantage of the narrow store selectors.
      this.patientFileMetadata = patientFileMetadata ?? [];
      // Set before the sort below: the comparator reads refKitLot to pin the reference lot first.
      this.refKitLot = refKitLot ?? '';
      const reportContentItems = reportContentResponse?.reportContentItems ?? [];
      const sortedReportContent = this.sortReportContent(reportContentItems);
      this.reportContent = sortedReportContent;
      this.filteredReportContent = sortedReportContent;
      this.sampleDataOriginal = sampleDataOriginal ?? [];
      this.sampleDataAdjusted = sampleDataAdjusted ?? [];
      this.calculationResponse = calculationResult ?? null;
      this.calculationAdjustedResponse = calculationAdjustedResult ?? null;
      this.panelSpecs = panelSpecs;
      this.operatorName = appState?.loggedInUser ?? '';
      this.studyName = appState?.selectedStudy?.studyName ?? '';
      this.specVersion = appState?.selectedStudy?.panelSpecs?.revisionNumber ?? '';
      this.appVersion = appState?.appVersion ?? '';
      this.calLots = calLots ?? [];
      this.qcLots = qcLots ?? [];
      this.runType = runType ?? '';
      this.testSpec = testSpec ?? '';

      const nextColumnSelectionKey = this.buildColumnSelectionKey(assayDetailsColumnSelection);
      if (this.shouldRebuildAssayTable(nextColumnSelectionKey)) {
        this.rebuildAssayTable();
        this.applyStoredColumnSelection(assayDetailsColumnSelection);

        if (this.isPrintView) {
          this.toggleAllSections(true);
          // Carries the operator's choice across the rebuild. `applyStoredColumnSelection` above
          // works from the screen's stored selection, which is empty on this route, so it has just
          // re-selected every column. `tableColumns` is corrected too, not only the data derived
          // from it: it is what the preview bar binds its checkboxes to.
          this.tableColumns = applyPrintColumnSelection(this.tableColumns, this.printColumnSelection);
          this.handleSelectColumnsDetails(this.tableColumns);
        }
      }
      this.cdRef.markForCheck();
      this.scheduleEmptyRowPrune();
    });

    this.store.select(selectSampleDataLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(sampleDataLoading => {
        this.sampleDataLoading = sampleDataLoading;
        this.cdRef.markForCheck();
      });
    /* eslint-enable @ngrx/avoid-combining-selectors */
  }

  /**
   * Lazily triggers pull_sample_data whenever a calculation makes cached rows stale.
   * Also shows a blocking spinner dialog whenever the backend call is running so the UI
   * never flashes empty tables while the data refreshes.
   */
  private monitorSampleDataStatus(): void {
    this.store.select(selectSampleDataStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        const becameStale = !status.sampleDataFresh && (this.lastSampleDataFresh === null || this.lastSampleDataFresh);
        if (becameStale && !status.sampleDataLoading) {
          this.store.dispatch(ensureSampleData());
        }
        this.lastSampleDataFresh = status.sampleDataFresh;
      });
  }

  ngAfterViewInit(): void {
    this.setupHeaderHeightObserver();
    this.scheduleEmptyRowPrune();
  }

  /**
   * Rebuilds the heavy table data whenever any of the store slices we care about changes.
   * Resets the cached arrays, regenerates the visible columns, and replays initialSetData so
   * we only do the expensive work when either the calculation payload or the selected tab changes.
   */
  private rebuildAssayTable(): void {
    this.sectionVisibility = new Array(15).fill(true);
    this.resetDeferredSections();
    this.calResponseFilteredData = [];
    this.calResponseFilteredAdjustedData = [];
    this.calResponseCombinedData = [];
    this.calResponseCombinedBackupData = [];
    this.calResponseDetails = [];
    this.samplesList = [];

    if (!this.filteredReportContent.length) {
      this.tableColumns = [];
      return;
    }

    const selections = new Map<string, ColumnSelection>();
    const reportContent = this.filteredReportContent;
    reportContent.forEach(item => {
      const kitLot = item.lot;
      const instrumentId = item.runId;
      const beadRegion = item.region;
      const columnKey = this.buildColumnKey(kitLot, instrumentId, beadRegion);
      if (!selections.has(columnKey)) {
        selections.set(columnKey, {
          columnName: kitLot,
          columnKey,
          kitLot,
          instrumentId,
          beadRegion,
          isSelected: true
        });
      }
    });

    const orderedReportContent = [...reportContent].sort((a, b) => this.compareReportContentItems(a, b));
    const orderByKey = new Map<string, number>();
    orderedReportContent.forEach((item, index) => {
      const key = this.buildColumnKey(item.lot, item.runId, item.region);
      if (!orderByKey.has(key)) {
        orderByKey.set(key, index);
      }
    });

    this.tableColumns = Array.from(selections.values()).sort((a, b) => {
      const aOrder = orderByKey.get(a.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderByKey.get(b.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    const originalSamplePool = this.sampleDataOriginal;
    this.initialSetData(this.calculationResponse, originalSamplePool, 'OriginalCalculate');

    if (this.calculationAdjustedResponse) {
      const adjustedSamplePool = this.sampleDataAdjusted;
      this.initialSetData(this.calculationAdjustedResponse, adjustedSamplePool, 'AdjustedCalculate');
    } else {
      this.calResponseFilteredAdjustedData = [];
      this.calResponseCombinedData = this.calResponseFilteredData;
      this.calResponseCombinedBackupData = this.calResponseFilteredData;
      this.calResponseDetails = this.calResponseCombinedData;
    }

    if (this.isPrintView) {
      this.handleSelectColumnsDetails(this.tableColumns);
    }

    this.scheduleDeferredSections();
  }

  private resetDeferredSections(): void {
    this.cancelDeferredSections();
    if (this.isPrintView || this.exportMode) {
      this.visibleSectionCount = this.sectionVisibility.length;
    } else {
      this.visibleSectionCount = Math.min(3, this.sectionVisibility.length);
    }
  }

  private scheduleDeferredSections(): void {
    if (this.isPrintView || this.exportMode) {
      return;
    }
    if (this.visibleSectionCount >= this.sectionVisibility.length) {
      return;
    }
    const token = ++this.deferredRenderToken;
    const runBatch = () => {
      if (token !== this.deferredRenderToken) {
        return;
      }
      const nextCount = Math.min(this.sectionVisibility.length, this.visibleSectionCount + 4);
      this.visibleSectionCount = nextCount;
      this.cdRef.markForCheck();
      this.scheduleEmptyRowPrune();
      if (this.visibleSectionCount < this.sectionVisibility.length) {
        this.queueDeferredRender(runBatch);
      }
    };
    this.queueDeferredRender(runBatch);
  }

  private queueDeferredRender(callback: () => void): void {
    this.deferredRenderHandle = window.setTimeout(callback, 16);
  }

  private cancelDeferredSections(): void {
    this.deferredRenderToken += 1;
    if (this.deferredRenderHandle !== null) {
      window.clearTimeout(this.deferredRenderHandle);
    }
    this.deferredRenderHandle = null;
  }

  private shouldRebuildAssayTable(nextColumnSelectionKey: string): boolean {
    if (!this.lastRebuildInputs) {
      this.lastRebuildInputs = {
        reportContent: this.reportContent,
        sampleDataOriginal: this.sampleDataOriginal,
        sampleDataAdjusted: this.sampleDataAdjusted,
        calculationResponse: this.calculationResponse,
        calculationAdjustedResponse: this.calculationAdjustedResponse,
        panelSpecs: this.panelSpecs,
        calLots: this.calLots,
        qcLots: this.qcLots,
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
      return true;
    }

    const last = this.lastRebuildInputs;
    const shouldRebuild = (
      last.reportContent !== this.reportContent ||
      last.sampleDataOriginal !== this.sampleDataOriginal ||
      last.sampleDataAdjusted !== this.sampleDataAdjusted ||
      last.calculationResponse !== this.calculationResponse ||
      last.calculationAdjustedResponse !== this.calculationAdjustedResponse ||
      last.panelSpecs !== this.panelSpecs ||
      last.calLots !== this.calLots ||
      last.qcLots !== this.qcLots ||
      last.runType !== this.runType ||
      last.testSpec !== this.testSpec ||
      last.refKitLot !== this.refKitLot ||
      last.columnSelectionKey !== nextColumnSelectionKey ||
      last.isPrintView !== this.isPrintView
    );

    if (shouldRebuild) {
      this.lastRebuildInputs = {
        reportContent: this.reportContent,
        sampleDataOriginal: this.sampleDataOriginal,
        sampleDataAdjusted: this.sampleDataAdjusted,
        calculationResponse: this.calculationResponse,
        calculationAdjustedResponse: this.calculationAdjustedResponse,
        panelSpecs: this.panelSpecs,
        calLots: this.calLots,
        qcLots: this.qcLots,
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
    }
    return shouldRebuild;
  }

  private buildColumnSelectionKey(selections: string[] | null | undefined): string {
    if (!selections?.length) {
      return '';
    }
    return selections
      .slice()
      .sort()
      .join('|');
  }
  /**
   * Prepares assay details or failures data based on calculation result.
   * @param calculationResult Parsed calculation payload for either original or adjusted runs
   * @param samplePool Cached pull_sample_data stream that matches the calculation payload
   * @param label Indicates whether it's original or adjusted data
  */
  initialSetData(
    calculationResult: CalculationResult | null,
    samplePool: SampleSummaryResult[],
    label: string
  ): void {
    const result: AssayDetails[] = [];
    const runStatusMap = this.getRunStatusMap(calculationResult?.runStatus);
    const regressionStatsMap = this.getRegressionStatsMap(calculationResult?.regressionStats);
    const reportItemsMap = this.getReportItemsMap(calculationResult?.reportContentItems);
    const samplesByReportId = this.getSamplesByReportId(samplePool);

    this.filteredReportContent.forEach((sample) => {
      const reportItemId = sample.reportContentItemId ?? '';
      const calReportContentItems = reportItemsMap.get(reportItemId);
      const regressionStat = regressionStatsMap.get(reportItemId);
      const calRunStatus = runStatusMap.get(reportItemId);
      const calSamples = samplesByReportId.get(reportItemId) ?? [];

      if (calRunStatus && calReportContentItems && regressionStat) {
        result.push({
          reportContentItems: sample,
          reportItems: calReportContentItems,
          runStatus: calRunStatus,
          regressionStats: regressionStat,
          samples: calSamples,
          valueAssigned: label
        });

      } else {
        result.push({
          reportContentItems: sample,
          runStatus: calRunStatus,
          regressionStats: {
            ranges: [],
            reportContentItemId: '',
            numPassSamples: 0,
            numAlertSamples: 0,
            numFailSamples: 0,
            averageBeadCV: 0,
            averageBeadCount: 0,
            averageCountTime: 0,
            percentPositiveAgreement: 0,
            percentPositiveAgreementCI: '',
            percentNegativeAgreement: 0,
            percentNegativeAgreementCI: '',
            numConcordanceSamples: 0,
            numTruePositives: 0,
            numFalsePositives: 0,
            numEQRef: 0,
            numTrueNegatives: 0,
            numFalseNegatives: 0,
            numEQTest: 0,
            numEQ: 0,
            negativeSamplesPASS: 0,
            negativeSamplesFAILED: 0,
            negativeSamplesALERT: 0,
            positiveSamplesPASS: 0,
            positiveSamplesFAILED: 0,
            positiveSamplesAlert: 0,
            cutOffSamplesPASS: 0,
            cutOffSamplesFail: 0,
            CutOffSamplesAlert: 0
          },
          samples: [],
          valueAssigned: label
        });
      }
    });

    if (label === 'OriginalCalculate') {
      // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
      this.calResponseFilteredData = this.isFinal ? [] : result;
    } else {
      this.calResponseFilteredAdjustedData = result;
    }
    if (this.calResponseFilteredAdjustedData.length > 0) {
      this.calResponseCombinedData = this.getCombinedData();
    } else {
      this.calResponseCombinedData = this.calResponseFilteredData;
    }
    this.samplesList = this.getUniqueAccessionNosCached();
    this.calResponseCombinedBackupData = this.calResponseCombinedData;
    this.calResponseDetails = this.calResponseCombinedData;
  }

  /**
   * Groups the cached pull_sample_data array by reportContentItemId once so we stop filtering
   * the entire dataset for every table cell.
   */
  private getSamplesByReportId(samples: SampleSummaryResult[] | undefined): Map<string, SampleSummaryResult[]> {
    if (!samples || samples.length === 0) {
      return new Map();
    }
    const cached = this.samplesByReportIdCache.get(samples);
    if (cached) {
      return cached;
    }
    const map = this.groupSamplesByReportId(samples);
    this.samplesByReportIdCache.set(samples, map);
    return map;
  }

  private groupSamplesByReportId(samples: SampleSummaryResult[]): Map<string, SampleSummaryResult[]> {
    const map = new Map<string, SampleSummaryResult[]>();
    samples.forEach(sample => {
      if (!sample.reportContentItemId) {
        return;
      }
      if (!map.has(sample.reportContentItemId)) {
        map.set(sample.reportContentItemId, []);
      }
      map.get(sample.reportContentItemId)?.push(sample);
    });
    return map;
  }

  /** Creates a dictionary for the runStatus array so lookups stay O(1) during table hydration. */
  private getRunStatusMap(runStatuses: RunStatus[] | undefined): Map<string, RunStatus> {
    if (!runStatuses || runStatuses.length === 0) {
      return new Map();
    }
    const cached = this.runStatusMapCache.get(runStatuses);
    if (cached) {
      return cached;
    }
    const map = this.buildRunStatusMap(runStatuses);
    this.runStatusMapCache.set(runStatuses, map);
    return map;
  }

  private buildRunStatusMap(runStatuses: RunStatus[] | undefined): Map<string, RunStatus> {
    const map = new Map<string, RunStatus>();
    runStatuses?.forEach(status => {
      if (status?.reportContentItemId) {
        map.set(status.reportContentItemId, status);
      }
    });
    return map;
  }

  /** Same as above but for regression stats, which power many of the failure summaries. */
  private getRegressionStatsMap(stats: RegressionStats[] | undefined): Map<string, RegressionStats> {
    if (!stats || stats.length === 0) {
      return new Map();
    }
    const cached = this.regressionStatsMapCache.get(stats);
    if (cached) {
      return cached;
    }
    const map = this.buildRegressionStatsMap(stats);
    this.regressionStatsMapCache.set(stats, map);
    return map;
  }

  private buildRegressionStatsMap(stats: RegressionStats[] | undefined): Map<string, RegressionStats> {
    const map = new Map<string, RegressionStats>();
    stats?.forEach(regression => {
      if (regression?.reportContentItemId) {
        map.set(regression.reportContentItemId, regression);
      }
    });
    return map;
  }

  /**
   * ReportItems do not carry the reportContentItemId, so we stringify their identifying fields
   * once (the same concatenation used throughout the legacy code) and store it in a lookup map.
   */
  private getReportItemsMap(reportItems: ReportItems[] | undefined): Map<string, ReportItems> {
    if (!reportItems || reportItems.length === 0) {
      return new Map();
    }
    const cached = this.reportItemsMapCache.get(reportItems);
    if (cached) {
      return cached;
    }
    const map = this.buildReportItemsMap(reportItems);
    this.reportItemsMapCache.set(reportItems, map);
    return map;
  }

  private buildReportItemsMap(reportItems: ReportItems[] | undefined): Map<string, ReportItems> {
    const map = new Map<string, ReportItems>();
    reportItems?.forEach(item => {
      map.set(this.buildReportItemKey(item), item);
    });
    return map;
  }

  /** Builds the string key calculatedReportContentItems used before (lot + bead region + instrument). */
  private buildReportItemKey(reportItem: ReportItems): string {
    return `${reportItem.kitLotNumber}${reportItem.BeadRegion}_${reportItem.instrumentID}`;
  }
  /**
   * Combines filtered original and adjusted data into a single dataset.
   * Used for display in the table.
   * @returns Merged array of AssayDetails
   */
  getCombinedData() {
    const combined = [
      ...(this.calResponseFilteredData ?? []),
      ...(this.calResponseFilteredAdjustedData ?? [])
    ];
    return combined.sort((left, right) => this.compareAssayDetails(left, right));
  }

  /**
   * Retrieves unique accession numbers from the currently combined assay data.
   * @returns Array of unique accession numbers
   */
  private getUniqueAccessionNosCached(): string[] {
    if (this.lastSamplesListSourceAll === this.calResponseCombinedData) {
      return this.lastSamplesListValueAll;
    }
    const allAccessionNos = this.calResponseCombinedData
      .map(item => item.samples)
      .flat()
      .map(sample => sample.accessionNo)
      .filter((accessionNo): accessionNo is string => accessionNo !== undefined);
    const uniqueAccessionNos = Array.from(new Set(allAccessionNos));
    this.lastSamplesListSourceAll = this.calResponseCombinedData;
    this.lastSamplesListValueAll = uniqueAccessionNos;
    return uniqueAccessionNos;
  }

  /**
   * Retrieves specific data for a sample by accession number and variable name.
   * @param materialItem Assay details containing samples
   * @param accessionNo Accession number of the sample
   * @param variableName Name of the variable to retrieve
   * @returns The value of the variable, or an empty string
   */
  getSampleData(materialItem: AssayDetails, accessionNo?: string, variableName?: string): string {
    if (!variableName) {
      return this.displayValue(null);
    }
    const sample = materialItem.samples.find(
      (cal: SampleSummaryResult) => cal.accessionNo === accessionNo
    );
    const value = this.getSampleFieldValue(sample, variableName);
    return this.displayValue(value, this.getColumnPrecision(materialItem));
  }

  /**
   * Derives the overall kit status based on various run validity flags.
   * @param materialItem AssayDetails containing run status information
   * @param name Name of the validation section ("RunValidity")
   * @returns The overall result status ("PASS", "FAIL", "ALERT")
   */
  getKitStatusRunValidity(materialItem: AssayDetails, name: string) {
    let overallStatus = "PASS";
    let results: (string | undefined)[] = [];
    if (name === "RunValidity") {
      results = [
        materialItem?.runStatus?.calibratorSpecStatus,
        materialItem?.runStatus?.patientSampleSpecStatus,
        materialItem?.runStatus?.calCurveInformation?.calCurveValidity,
        materialItem?.runStatus?.calCurveInformation?.calReplicateValidity,
        materialItem?.runStatus?.sampleReplicateCountValidity
      ];
    }
    const filteredResults = results.filter((result): result is string => result !== undefined);
    overallStatus = this.resultPassFailAlert(filteredResults);
    return overallStatus;
  }

  /**
   * Toggles visibility for a specific section in the UI.
   * @param sectionIndex Index of the section to toggle
   */
  toggleSectionDisplay(sectionIndex: number) {
    this.sectionVisibility[sectionIndex] = !this.sectionVisibility[sectionIndex];
    this.scheduleEmptyRowPrune();
  }
  /**
   * Expands or collapses all sections in the UI.
   * @param isExpand True to expand all, false to collapse all
   */
  toggleAllSections(isExpand: boolean) {
    this.sectionVisibility = this.sectionVisibility.map(() => isExpand);
    if (isExpand) {
      this.visibleSectionCount = this.sectionVisibility.length;
    }
    this.scheduleEmptyRowPrune();
  }

  private scheduleEmptyRowPrune(): void {
    if (!this.assayDetailsTableRef?.nativeElement) {
      return;
    }
    window.setTimeout(() => this.pruneEmptyRows(), 0);
  }

  private pruneEmptyRows(): void {
    const tables = this.assayDetailsTableRefs?.toArray().map(ref => ref.nativeElement)
      ?? (this.assayDetailsTableRef ? [this.assayDetailsTableRef.nativeElement] : []);
    tables.forEach(tableEl => this.pruneEmptyRowsIn(tableEl));
  }

  private pruneEmptyRowsIn(tableEl: HTMLTableElement): void {
    const rows = Array.from(tableEl.querySelectorAll('tr')) as HTMLTableRowElement[];
    rows.forEach(row => {
      if (
        row.classList.contains('subsection-header') ||
        row.classList.contains('subsection-header-print2') ||
        row.classList.contains('subsection-header-print') ||
        row.classList.contains('subsection-header-print-firstrow')
      ) {
        row.style.removeProperty('display');
        return;
      }
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length <= 1) {
        row.style.removeProperty('display');
        return;
      }
      const dataCells = cells.slice(1);
      const hasValue = dataCells.some(cell => {
        const text = (cell.textContent ?? '').trim();
        return text !== '' && text !== '-';
      });
      row.style.display = hasValue ? '' : 'none';
    });
  }

  shouldRenderSection(index: number): boolean {
    return index < this.visibleSectionCount;
  }
  /**
   * Filters assay details for the "Assay Details" tab based on selected columns.
   * @param selectedColumns Columns that are currently selected in the UI
   */
  handleSelectColumnsDetails(selectedColumns: ColumnSelection[]) {
    this.selectedColumns = selectedColumns;
    this.calResponseCombinedData = this.calResponseDetails.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
    if (!this.isPrintView && !this.exportMode) {
      this.persistColumnSelection(selectedColumns);
    }
  }
  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumnsDetails(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.runType,
      this.testSpec ? `Test Spec ${this.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  private applyStoredColumnSelection(columnKeys: string[] | null | undefined): void {
    if (!this.tableColumns?.length) {
      this.selectedColumns = [];
      return;
    }
    const selectionSet = new Set((columnKeys ?? []).filter(Boolean));
    const hasMatches = this.tableColumns.some(column =>
      selectionSet.has(column.columnKey ?? column.columnName ?? '')
    );
    const useDefault = selectionSet.size === 0 || !hasMatches;
    this.tableColumns = this.tableColumns.map(column => ({
      ...column,
      isSelected: useDefault ? true : selectionSet.has(column.columnKey ?? column.columnName ?? '')
    }));
    this.selectedColumns = this.tableColumns;
    this.calResponseCombinedData = this.calResponseDetails.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
  }

  private persistColumnSelection(columns: ColumnSelection[]): void {
    const columnKeys = (columns ?? [])
      .filter(column => column.isSelected)
      .map(column => column.columnKey ?? column.columnName ?? '')
      .filter(key => key.length > 0);
    this.store.dispatch(setAssayDetailsColumnSelection({ columnKeys }));
  }
  /**
   * Handles the open/close status of the print menu.
   * If opened, expands all sections.
   * @param isMenuOpen Boolean indicating whether the print menu is open
   */
  handleMenuOpenStatus(isMenuOpen: boolean) {
    this.isPrintMode = isMenuOpen;

    if (isMenuOpen) {
      this.toggleAllSections(isMenuOpen);
    }
  }
  /**
   * Returns the current date formatted for display.
   * @returns A formatted date string
   */
  getDate(): string {
    return this.appUtilityService.formatDate(new Date());
  }

  private matchesSelection(column: ColumnSelection, reportContentItem: ReportContentItem): boolean {
    if (!column.isSelected) {
      return false;
    }

    if (!column.columnKey && !column.instrumentId && column.beadRegion === undefined) {
      return column.columnName === reportContentItem.lot;
    }

    const key = this.buildColumnKey(reportContentItem.lot, reportContentItem.runId, reportContentItem.region);
    return (column.columnKey ?? column.columnName) === key;
  }

  private buildColumnKey(kitLot: string | undefined, instrumentId: string | undefined, beadRegion: number | undefined): string {
    return `${kitLot ?? ''}||${instrumentId ?? ''}||${beadRegion ?? ''}`;
  }

  private sortReportContent(reportContent: ReportContentItem[]): ReportContentItem[] {
    return [...(reportContent ?? [])].sort((a, b) => this.compareReportContentItems(a, b));
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustmentCompare =
      this.getReportContentAdjustmentRank(a) - this.getReportContentAdjustmentRank(b);
    if (adjustmentCompare !== 0) {
      return adjustmentCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }
    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareAssayDetails(left: AssayDetails, right: AssayDetails): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare = this.getAssayAdjustmentRank(left) - this.getAssayAdjustmentRank(right);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const runCompareWithMean = this.compareRunIdWithMeanLast(left, right);
    if (runCompareWithMean !== 0) {
      return runCompareWithMean;
    }

    return 0;
  }

  private compareRunIdWithMeanLast(left: AssayDetails, right: AssayDetails): number {
    const leftIsMean = this.isMeanReportItem(left.reportContentItems.reportContentItemId);
    const rightIsMean = this.isMeanReportItem(right.reportContentItems.reportContentItemId);
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getAssayAdjustmentRank(item: AssayDetails): number {
    return item.valueAssigned === 'AdjustedCalculate' ? 1 : 0;
  }

  private getReportContentAdjustmentRank(item: ReportContentItem): number {
    return item?.['valueAssigned'] === 'AdjustedCalculate' ? 1 : 0;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Retrieves metadata value for a given key from the first patient file.
   * @param key Key of the metadata field
   * @returns Value of the specified metadata field or an empty string
   */
  getPatientFileData<K extends keyof FileMetaData>(key: K): string {
    if (this.patientFileMetadata.length) {
      return this.displayValue(this.patientFileMetadata[0][key]);
    }
    return this.displayValue(null);
  }
  /**
   * Gets the unit string associated with a given bead region number.
   * @param region The bead region number
   * @returns The corresponding unit string or an empty string
   */
  getUnitsByRegion(region: number): string {
    if (!this.panelSpecs || !Array.isArray(this.panelSpecs?.Assay_Specs)) {
      return '';
    }

    const match = this.panelSpecs?.Assay_Specs.find(spec => spec.bead_region === region);
    return match?.units ?? '';
  }
  /**
   * Returns the status color for a sample. Failures are colored red.
   * @param materialItem AssayDetails object containing sample data
   * @param accessionNo Accession number of the sample
   * @returns A color string for styling (e.g. red for failure)
   */
  getSampleStatusClass(materialItem: AssayDetails, accessionNo: string): string {
    const sample = materialItem.samples.find(s => s.accessionNo === accessionNo);
    if (sample?.resultStatus?.toLowerCase() === 'fail') {
      return 'bpx-status--fail';
    }
    return '';
  }

  /**
   * Formats values for display in the template, substituting '-' for blanks.
   * @param value Raw value from the dataset
   * @returns Formatted string suitable for rendering
   */
  displayValue(value: unknown, precision?: number): string {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none') {
        return '-';
      }
      return trimmed;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        return '-';
      }
      if (typeof precision === 'number' && precision >= 0) {
        return this.formatNumberWithPrecision(value, precision);
      }
      return value.toString();
    }
    return String(value);
  }

  private formatNumberWithPrecision(value: number, precision: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }
    const maxDecimals = Math.min(Math.max(precision, 0), 6);
    const factor = 10 ** maxDecimals;
    const truncated = Math.round(value * factor) / factor;
    const fixed = truncated.toFixed(maxDecimals);
    // Only trim a fractional tail — at precision 0 there is no decimal point, and stripping
    // trailing zeros from a whole number would turn 100 into 1.
    return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
  }

  getSectionStatusLabel(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    const source = this.calResponseCombinedData;
    const statuses = this.collectSectionStatuses(section, source);
    let hasFail = false;
    let hasAlert = false;
    let hasPass = false;
    statuses.forEach(status => {
      const normalized = normalizeStatusValue(status);
      if (normalized === 'FAIL') {
        hasFail = true;
      } else if (normalized === 'ALERT') {
        hasAlert = true;
      } else if (normalized === 'PASS') {
        hasPass = true;
      }
    });
    if (hasFail) {
      return 'FAIL';
    }
    if (hasAlert) {
      return 'ALERT';
    }
    if (hasPass) {
      return 'PASS';
    }
    return 'N/A';
  }

  getSectionStatusClass(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    const status = this.getSectionStatusLabel(section);
    if (status === 'FAIL') {
      return 'status-label status-label--fail';
    }
    if (status === 'ALERT') {
      return 'status-label status-label--alert';
    }
    if (status === 'PASS') {
      return 'status-label status-label--pass';
    }
    return 'status-label status-label--na';
  }

  private collectSectionStatuses(
    section: 'concentration' | 'score' | 'sdCv' | 'replicates',
    source: AssayDetails[]
  ): string[] {
    const statuses: string[] = [];
    const fieldName = this.getSectionStatusField(section);
    source.forEach(item => {
      (item.samples ?? []).forEach(sample => {
        if (!fieldName) {
          return;
        }
        const value = this.getSampleFieldValue(sample, fieldName);
        if (typeof value === 'string') {
          statuses.push(value);
        } else if (typeof value === 'number') {
          statuses.push(String(value));
        }
      });
    });
    return statuses;
  }

  private getSectionStatusField(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    switch (section) {
      case 'concentration':
      case 'score':
        return 'resultStatus';
      case 'sdCv':
        return 'resultRepOk';
      case 'replicates':
        return 'repCountOk';
    }
  }

  private getSampleFieldValue(sample: SampleSummaryResult | undefined, variableName: string): unknown {
    if (!sample) {
      return undefined;
    }
    const record = sample as Record<string, unknown>;
    if (variableName in record) {
      return record[variableName];
    }
    const alias = this.sampleFieldAliases[variableName];
    if (alias && alias in record) {
      return record[alias];
    }
    return undefined;
  }

  private getColumnPrecision(materialItem: AssayDetails): number | undefined {
    const precision = materialItem?.reportContentItems?.userDefinedDecimalPrecision
      ?? materialItem?.reportContentItems?.resultDecimalPrecision;
    return typeof precision === 'number' ? precision : undefined;
  }
  /**
   * Exports the assay table currently rendered in the DOM to a CSV file.
   */
  exportTableToCsv(): void {
    const table = document.getElementById(this.tableId) as HTMLTableElement;
    if (!table) {
      console.error('Table not found!');
      return;
    }

    let csv = '';
    for (const row of Array.from(table.rows)) {
      const cols = Array.from(row.cells).map(cell => {
        let text = cell.innerText.trim().replace(/"/g, '""');
        if (text.includes(',') || text.includes('"') || text.includes('\n')) {
          text = `"${text}"`;
        }
        return text;
      });
      csv += cols.join(',') + '\n';
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'assays-table-export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  /**
   * Navigates to the Assay Details print view route.
   */
  navigateToPrintView() {
    this.router.navigateByUrl('/(print:print/preview-assay-details)');
  }

  exportAsWorkbook(): void {
    void this.assayWorkbookExportService.exportWorkbook({
      truncateSummary: this.printTruncatedReport
    });
  }

  private setupHeaderHeightObserver(): void {
    const scrollEl = this.assayDetailsScrollRef?.nativeElement;
    const tableEl = this.assayDetailsTableRef?.nativeElement;
    if (!scrollEl || !tableEl) {
      return;
    }

    const headerEl = tableEl.querySelector('thead');
    if (!headerEl) {
      return;
    }

    const updateHeaderHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      if (height > 0) {
        scrollEl.style.setProperty('--assay-details-header-height', `${Math.ceil(height)}px`);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(() => updateHeaderHeight());
      this.headerResizeObserver.observe(headerEl);
    } else {
      const onResize = () => updateHeaderHeight();
      window.addEventListener('resize', onResize);
      this.destroy$.subscribe(() => window.removeEventListener('resize', onResize));
    }
  }
  /**
    * Lifecycle hook: Component cleanup.
    * Ensures subscriptions are properly unsubscribed.
    */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.headerResizeObserver?.disconnect();
    this.cancelDeferredSections();
  }
}
